import { useMemo, useState } from 'react'
import { Box, ButtonBase, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Button, Card, Input, Modal, Skeleton } from '@repo/ui'
import { SelectField } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle } from 'components/input/ViewToggle/ViewToggle'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useCompetitorFeatures,
  useCreateCompetitorFeature,
  usePatchCompetitorFeature,
  useSetFeatureCell,
} from 'hooks/api'
import type { CompetitorFeature, FeatureCell, FeatureCellValue, ResearchCompetitor, VeraFeatureStatus } from 'services'

import {
  Cell,
  EmptyRow,
  HeadCell,
  Notice,
  SectionNote,
  StatTable,
  ToneChip,
  type Tone,
} from '../InternalAnalyticsPage/components/shared'
import { readOnDate, todayLocal } from './captureDate'
import { CompetitorLink } from './CompetitorLink'

const ADMIN_ONLY = 'Only platform admins can change competitor research.'

const CATEGORY_LABEL: Record<string, string> = { tags: 'Tags', certificates: 'Certificates', platform: 'Platform' }
const categoryLabel = (c: string) => CATEGORY_LABEL[c] ?? c.charAt(0).toUpperCase() + c.slice(1)

const CELL: Record<FeatureCellValue, { label: string; tone: Tone }> = {
  yes: { label: 'Yes', tone: 'positive' },
  partial: { label: 'Partial', tone: 'caution' },
  no: { label: 'No', tone: 'neutral' },
  unknown: { label: 'Unknown', tone: 'neutral' },
}
const VERA: Record<VeraFeatureStatus, { label: string; tone: Tone; glossaryId: string }> = {
  shipped: { label: 'Shipped', tone: 'positive', glossaryId: 'vera-feature-shipped' },
  planned: { label: 'Planned', tone: 'info', glossaryId: 'vera-feature-planned' },
}

/**
 * VRG-744 (Spencer, 2026-10-29) — every approved competitor is a column, and the table scrolls
 * sideways inside the card. It used to show four and make you pick the rest, on the argument that
 * a 30-column matrix is unreadable. That was answering the wrong question: a matrix is for
 * COMPARING, and four columns chosen by "who happens to have a cell" compares nothing. Scrolling
 * is the right cost, as long as two things hold — the order has to be meaningful, and you must
 * still know which row you are on once the left edge is gone. Hence the sort below and the two
 * pinned columns.
 *
 * `riskRank` is "N of 10", LOWER = more dangerous, null = not judged yet. `?? 99` parks the
 * unjudged ones at the far right, which is the convention `SeoGapsCard` already uses. `kind`
 * breaks a tie, since a business rival at rank 4 outranks an adjacent one at rank 4; the name
 * breaks the rest so the order does not move between loads.
 */
const KIND_WEIGHT: Record<ResearchCompetitor['kind'], number> = { business: 0, search: 1, adjacent: 2 }
const byRisk = (a: ResearchCompetitor, b: ResearchCompetitor) =>
  (a.riskRank ?? 99) - (b.riskRank ?? 99) ||
  KIND_WEIGHT[a.kind] - KIND_WEIGHT[b.kind] ||
  (a.name ?? a.domain).localeCompare(b.name ?? b.domain)

/**
 * The two pinned columns. These are px, not theme units, because they are also the `left` offset
 * of the second pinned column — the sticky arithmetic has to agree with the rendered width or the
 * Vera column lands on top of Feature mid-scroll.
 */
const FEATURE_W = 300
const VERA_W = 120
/** `sticky` only takes effect against a background; without one the scrolled cells show through. */
const stickySx = (left: number, width: number) => ({
  position: 'sticky' as const,
  left,
  zIndex: 2,
  width,
  minWidth: width,
  maxWidth: width,
  backgroundColor: 'background.paper',
})

/** Edit one competitor × feature cell. A known value is a claim, so it needs a source and a date. */
const CellModal = ({
  target,
  onClose,
}: {
  target: { feature: CompetitorFeature; competitor: ResearchCompetitor; cell: FeatureCell | null } | null
  onClose: () => void
}) => {
  const set = useSetFeatureCell()
  const { open: notify } = useSnackbar()
  const [value, setValue] = useState<FeatureCellValue>(target?.cell?.value ?? 'unknown')
  const [note, setNote] = useState(target?.cell?.note ?? '')
  const [sourceUrl, setSourceUrl] = useState(target?.cell?.sourceUrl ?? '')
  const [capturedAt, setCapturedAt] = useState(target?.cell?.capturedAt?.slice(0, 10) ?? todayLocal())
  const [error, setError] = useState<string | null>(null)
  if (!target) return null
  const claim = value !== 'unknown'
  const ready = !claim || Boolean(sourceUrl.trim() && capturedAt)

  const submit = () =>
    set.mutate(
      {
        featureId: target.feature.id,
        domain: target.competitor.domain,
        input: {
          value,
          note: note.trim() || null,
          sourceUrl: sourceUrl.trim() || null,
          capturedAt: claim || sourceUrl.trim() ? capturedAt : null,
        },
      },
      {
        onSuccess: () => {
          notify({ type: 'success', text: `Saved ${target.competitor.name ?? target.competitor.domain} · ${target.feature.name}.` })
          onClose()
        },
        onError: (e) => setError((e as Error).message),
      },
    )

  return (
    <Modal
      open
      onClose={onClose}
      title={`${target.competitor.name ?? target.competitor.domain} · ${target.feature.name}`}
      description="What the competitor's own page says about this capability. Leave it unknown until you have a source and a date."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!ready} loading={set.isPending}>
            Save cell
          </Button>
        </>
      }
    >
      {error ? (
        <Box sx={{ mb: 3 }}>
          <Notice severity="error">{error}</Notice>
        </Box>
      ) : null}
      <Stack spacing={4}>
        <SelectField
          label="Value"
          value={value}
          onChange={(v) => setValue(v as FeatureCellValue)}
          options={(Object.keys(CELL) as FeatureCellValue[]).map((v) => ({ value: v, label: CELL[v].label }))}
          disabled={set.isPending}
        />
        <Input
          label="Source page"
          placeholder="https://example.com/features"
          value={sourceUrl}
          onChange={setSourceUrl}
          disabled={set.isPending}
          helperText={claim ? 'Required for any value but unknown.' : undefined}
        />
        <Input label="Read on" type="date" value={capturedAt} onChange={setCapturedAt} disabled={set.isPending} />
        <Input
          label="Note"
          placeholder="What the page says, in its terms (optional)"
          value={note}
          onChange={setNote}
          multiline
          minRows={2}
          disabled={set.isPending}
        />
      </Stack>
    </Modal>
  )
}

/** Vera's own cell: shipped (on main today), planned (announced), or not claimed. */
const VeraModal = ({ feature, onClose }: { feature: CompetitorFeature | null; onClose: () => void }) => {
  const patch = usePatchCompetitorFeature()
  const { open: notify } = useSnackbar()
  const [status, setStatus] = useState<VeraFeatureStatus | 'none'>(feature?.veraStatus ?? 'none')
  const [source, setSource] = useState(feature?.veraSource ?? '')
  const [description, setDescription] = useState(feature?.description ?? '')
  const [note, setNote] = useState(feature?.veraNote ?? '')
  const [error, setError] = useState<string | null>(null)
  if (!feature) return null
  /* A planned row is published with this note as its provenance; without one it would read as shipped. */
  const ready = status !== 'planned' || Boolean(note.trim())
  const submit = () =>
    patch.mutate(
      {
        id: feature.id,
        patch: {
          veraStatus: status === 'none' ? null : status,
          veraSource: source.trim() || null,
          veraNote: note.trim() || null,
          description: description.trim() || null,
        },
      },
      {
        onSuccess: () => {
          notify({ type: 'success', text: `Saved Vera · ${feature.name}.` })
          onClose()
        },
        onError: (e) => setError((e as Error).message),
      },
    )
  return (
    <Modal
      open
      onClose={onClose}
      title={`Vera · ${feature.name}`}
      description="Shipped means a customer can use it on Vera today. Planned means it is announced. Say what Vera does; never what it does not."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!ready} loading={patch.isPending}>
            Save
          </Button>
        </>
      }
    >
      {error ? (
        <Box sx={{ mb: 3 }}>
          <Notice severity="error">{error}</Notice>
        </Box>
      ) : null}
      <Stack spacing={4}>
        <SelectField
          label="Vera"
          value={status}
          onChange={(v) => setStatus(v as VeraFeatureStatus | 'none')}
          options={[
            { value: 'shipped', label: 'Shipped', description: 'On Vera today, reachable by a customer.' },
            { value: 'planned', label: 'Planned', description: 'Announced, not shipped yet.' },
            { value: 'none', label: 'Not claimed', description: 'Neither shipped nor announced. Not published.' },
          ]}
          disabled={patch.isPending}
        />
        <Input
          label="Description"
          value={description}
          onChange={setDescription}
          multiline
          minRows={2}
          disabled={patch.isPending}
          helperText={feature.webList ? 'This text is published on /compare when the website reads the generated rows.' : undefined}
        />
        <Input
          label="Evidence"
          placeholder="A path:line in vera-2, or a link (optional)"
          value={source}
          onChange={setSource}
          disabled={patch.isPending}
        />
        <Input
          label="Note"
          placeholder="Who announced it, and when"
          value={note}
          onChange={setNote}
          multiline
          minRows={2}
          disabled={patch.isPending}
          helperText={status === 'planned' ? 'Required for planned: it is published as the row\'s provenance.' : undefined}
        />
      </Stack>
    </Modal>
  )
}

const AddFeatureModal = ({ open, onClose, categories }: { open: boolean; onClose: () => void; categories: string[] }) => {
  const create = useCreateCompetitorFeature()
  const { open: notify } = useSnackbar()
  const [name, setName] = useState('')
  const [category, setCategory] = useState(categories[0] ?? 'platform')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const close = () => {
    setName('')
    setDescription('')
    setError(null)
    onClose()
  }
  const submit = () =>
    create.mutate(
      { name: name.trim(), category, description: description.trim() || null },
      {
        onSuccess: (f) => {
          notify({ type: 'success', text: `Added ${f.name}. It is research only until it is added to /compare.` })
          close()
        },
        onError: (e) => setError((e as Error).message),
      },
    )
  return (
    <Modal
      open={open}
      onClose={close}
      title="Add feature"
      description="A capability row for the matrix. Rows added here are research: only rows that came from the website's /compare list are written back to it."
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim()} loading={create.isPending}>
            Add feature
          </Button>
        </>
      }
    >
      {error ? (
        <Box sx={{ mb: 3 }}>
          <Notice severity="error">{error}</Notice>
        </Box>
      ) : null}
      <Stack spacing={4}>
        <Input label="Feature" value={name} onChange={setName} disabled={create.isPending} />
        <SelectField
          label="Category"
          value={category}
          onChange={(v) => setCategory(v as string)}
          options={categories.map((c) => ({ value: c, label: categoryLabel(c) }))}
          disabled={create.isPending}
        />
        <Input label="Description" value={description} onChange={setDescription} multiline minRows={2} disabled={create.isPending} />
      </Stack>
    </Modal>
  )
}

/**
 * VRG-744 — the Features tab: a capability matrix. Rows are features by category; Vera's column is
 * shipped / planned; every competitor column is yes / partial / no / unknown, and only a cell with
 * a source and a date is anything but unknown.
 */
export const CompetitorFeaturesTab = ({ addOpen, onCloseAdd }: { addOpen: boolean; onCloseAdd: () => void }) => {
  const { data, isLoading, error } = useCompetitorFeatures()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const [category, setCategory] = useState<string>('all')
  const [picked, setPicked] = useState<string[] | null>(null)
  const [cellTarget, setCellTarget] = useState<{
    feature: CompetitorFeature
    competitor: ResearchCompetitor
    cell: FeatureCell | null
  } | null>(null)
  const [veraTarget, setVeraTarget] = useState<CompetitorFeature | null>(null)

  const categories = useMemo(() => [...new Set((data?.features ?? []).map((f) => f.category))], [data])
  const cellOf = useMemo(() => {
    const m = new Map<string, FeatureCell>()
    for (const c of data?.cells ?? []) m.set(`${c.featureId}|${c.competitorDomain}`, c)
    return m
  }, [data])

  if (isLoading)
    return (
      <Card>
        <Skeleton height={14} lines={10} />
      </Card>
    )
  if (error || !data)
    return (
      <Card>
        <SectionNote>{(error as Error | null)?.message ?? 'Could not load the feature matrix.'}</SectionNote>
      </Card>
    )

  // Every approved competitor, most dangerous first. The picker narrows this; it no longer has to
  // be used before the matrix says anything. A picked subset keeps the same order, so a column
  // does not move when you drop the one beside it.
  const shown = (picked ? data.competitors.filter((c) => picked.includes(c.domain)) : [...data.competitors]).sort(
    byRisk,
  )
  const rows = data.features.filter((f) => category === 'all' || f.category === category)
  const known = data.cells.filter((c) => c.value !== 'unknown').length

  return (
    <Stack spacing={6}>
      <Card title="Feature matrix">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <ViewToggle
              options={[{ value: 'all', label: 'All' }, ...categories.map((c) => ({ value: c, label: categoryLabel(c) }))]}
              value={category}
              onChange={setCategory}
              ariaLabel="Category"
            />
            <Box sx={{ minWidth: 280 }}>
              <SelectField
                multiple
                search
                valuePrefix="Competitors"
                placeholder="Competitors"
                value={shown.map((c) => c.domain)}
                onChange={(v) => setPicked(v as string[])}
                options={data.competitors.map((c) => ({ value: c.domain, label: c.name ?? c.domain }))}
              />
            </Box>
          </Stack>
          <SectionNote>
            {data.features.length} features &times; {shown.length} competitors, {known} sourced{' '}
            {known === 1 ? 'cell' : 'cells'}. Columns run
            most dangerous first by risk rank, unranked last; scroll sideways for the rest, and Feature and Vera stay
            put while you do. Vera&rsquo;s column says what Vera ships today or has announced. A competitor cell is
            unknown until it has a source page and a date; the date is on hover.
            {isAdmin ? ' Click a cell to edit it.' : ''}
          </SectionNote>
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell sx={stickySx(0, FEATURE_W)}>Feature</HeadCell>
                <HeadCell sx={stickySx(FEATURE_W, VERA_W)}>Vera</HeadCell>
                {shown.map((c) => (
                  <HeadCell key={c.domain}>
                    {/* Long names ("Aura Blockchain Consortium") wrap instead of widening the column. */}
                    <Box sx={{ whiteSpace: 'normal', maxWidth: 120, lineHeight: 1.4 }}>
                      <CompetitorLink domain={c.domain} name={c.name} />
                    </Box>
                  </HeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <EmptyRow
                  colSpan={2 + shown.length}
                  text="No features yet. Run the compare.ts import, or add one."
                />
              ) : (
                rows.map((f, i) => {
                  const header = i === 0 || rows[i - 1].category !== f.category
                  return [
                    header && category === 'all' ? (
                      <TableRow key={`h-${f.category}`}>
                        <Cell strong sx={stickySx(0, FEATURE_W)}>
                          <Typography variant="body3" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                            {categoryLabel(f.category)}
                          </Typography>
                        </Cell>
                        <Cell sx={stickySx(FEATURE_W, VERA_W)}> </Cell>
                        {shown.map((c) => (
                          <Cell key={c.domain}> </Cell>
                        ))}
                      </TableRow>
                    ) : null,
                    <TableRow key={f.id}>
                      <Cell sx={stickySx(0, FEATURE_W)}>
                        <Box sx={{ whiteSpace: 'normal', lineHeight: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {f.name}
                          </Typography>
                          {f.description ? (
                            <Typography variant="body3" color="text.secondary">
                              {f.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Cell>
                      <Cell sx={stickySx(FEATURE_W, VERA_W)}>
                        <InfoTooltip text={isAdmin ? 'Edit Vera’s cell' : ADMIN_ONLY}>
                          <ButtonBase
                            disabled={!isAdmin}
                            onClick={() => setVeraTarget(f)}
                            sx={{ borderRadius: 2 }}
                            aria-label={`Vera, ${f.name}`}
                          >
                            {f.veraStatus ? (
                              <ToneChip tone={VERA[f.veraStatus].tone} label={VERA[f.veraStatus].label} glossaryId={VERA[f.veraStatus].glossaryId} />
                            ) : (
                              <ToneChip tone="neutral" label="Not claimed" />
                            )}
                          </ButtonBase>
                        </InfoTooltip>
                      </Cell>
                      {shown.map((c) => {
                        const cell = cellOf.get(`${f.id}|${c.domain}`) ?? null
                        const v = cell?.value ?? 'unknown'
                        const tip =
                          v === 'unknown'
                            ? isAdmin
                              ? 'Unknown. Click to record what their page says.'
                              : 'Unknown.'
                            : `Read on ${readOnDate(cell?.capturedAt ?? null)} from ${cell?.sourceUrl ?? ''}${cell?.note ? `. ${cell.note}` : ''}`
                        return (
                          <Cell key={c.domain}>
                            <InfoTooltip text={tip}>
                              <ButtonBase
                                disabled={!isAdmin}
                                onClick={() => setCellTarget({ feature: f, competitor: c, cell })}
                                sx={{ borderRadius: 2 }}
                                aria-label={`${c.name ?? c.domain}, ${f.name}: ${CELL[v].label}`}
                              >
                                <ToneChip tone={CELL[v].tone} label={CELL[v].label} />
                              </ButtonBase>
                            </InfoTooltip>
                          </Cell>
                        )
                      })}
                    </TableRow>,
                  ]
                })
              )}
            </TableBody>
          </StatTable>
        </Stack>
      </Card>
      {/*
        The key remounts each modal so it re-reads its draft state from the new target. The idle
        fallbacks must DIFFER: both were 'none', which made two siblings share a key, and React
        may omit or duplicate children in that case.
      */}
      <CellModal key={cellTarget ? `cell|${cellTarget.feature.id}|${cellTarget.competitor.domain}` : 'cell-idle'} target={cellTarget} onClose={() => setCellTarget(null)} />
      <VeraModal key={veraTarget ? `vera|${veraTarget.id}` : 'vera-idle'} feature={veraTarget} onClose={() => setVeraTarget(null)} />
      <AddFeatureModal open={addOpen} onClose={onCloseAdd} categories={categories.length ? categories : ['tags', 'certificates', 'platform']} />
    </Stack>
  )
}
